import { useConfiguratorStore } from '@store/useConfiguratorStore';
import './PrintAreaSelector.css';

// Product-aware Icon Component
const PrintAreaIcon = ({ view, productId }: { view: string, productId: string }) => {
    // Map View + Product to specific SVG Filenames
    // Note: Paths are relative to public/ folder
    
    let iconPath = '';

    // Normalize input for easier matching
    const v = view.toLowerCase();
    const p = productId.toLowerCase();

    // 0. All Faces (Universal for any product)
    if (v.includes('all faces') || v.includes('all-faces')) {
        iconPath = '/images/SVG/Square%20Pen%20Print%20Area.svg'; // Generic icon for all faces
    }
    // 1. Sport Jersey
    if (p.includes('jersey')) {
        if (v.includes('chest') || (v.includes('left') && v.includes('front'))) iconPath = '/images/SVG/Jersey right front Print Area.svg'; // User requested this specific SVG for left chest
        else if (v.includes('front') && v.includes('right')) iconPath = '/images/SVG/Jersey right front Print Area.svg';
        else if (v.includes('front')) iconPath = '/images/SVG/Jersey front Print Area.svg';
        else if (v.includes('back')) iconPath = '/images/SVG/Jersey black Print Area.svg'; 
        else if (v.includes('left') && v.includes('sleeve')) iconPath = '/images/SVG/Jersey Left Sleeve Print Area.svg';
        else if (v.includes('right') && v.includes('sleeve')) iconPath = '/images/SVG/Jersey Right Sleeve Print Area.svg';
    }
    // 2. Polo T-Shirt
    else if (p.includes('polo') || p.includes('shirt')) {
        if (v.includes('chest') || (v.includes('left') && v.includes('front'))) iconPath = '/images/SVG/Jersey right front Print Area.svg'; // User requested this specific SVG for left chest
        else if (v.includes('front')) iconPath = '/images/SVG/T shirt front Print Area.svg';
        else if (v.includes('back')) iconPath = '/images/SVG/T shirt black Print Area.svg';
        else if (v.includes('left') && v.includes('sleeve')) iconPath = '/images/SVG/T shirt Left Sleeve Print Area.svg';
        else if (v.includes('right') && v.includes('sleeve')) iconPath = '/images/SVG/T shirt Right Sleeve Print Area.svg';
    }
    // 3. Glass Award
    else if (p.includes('award') || p.includes('glass')) {
        iconPath = '/images/SVG/Award Front Print Area.svg';
    }
    // 4. Diary
    else if (p.includes('diary')) {
        // Use Book icon for ANY print area on the diary
        iconPath = '/images/SVG/Book Print Area.svg';
    }
    // 5. Cap
    else if (p.includes('cap')) {
        if (v.includes('strip')) iconPath = '/images/SVG/Back Cap Strip Print Area.svg';
        else if (v.includes('front')) iconPath = '/images/SVG/Cap Front Panel Print Area.svg';
        else if (v.includes('back')) iconPath = '/images/SVG/Back Cap Print Area.svg';
        else if (v.includes('left')) iconPath = '/images/SVG/Cap Left Panel Print Area.svg';
        else if (v.includes('right')) iconPath = '/images/SVG/Cap Right Panel Print Area.svg';
        else iconPath = '/images/SVG/Cap Front Panel Print Area.svg'; // Default to front panel
    }
    // 6. Towel
    else if (p.includes('towel')) {
        iconPath = '/images/SVG/Golf Towel.svg';
    }
    // 7. Pen (Generic + Performance)
    else if (p.includes('pen')) {
        if (p.includes('performance') || p.includes('square')) {
             if (v.includes('front')) iconPath = '/images/SVG/Square%20Pen%20Print%20Area.svg'; 
             else if (v.includes('back')) iconPath = '/images/SVG/Pen%20Back%20Print%20Area.svg';
             else if (v.includes('side')) iconPath = '/images/SVG/Pen%20Side%20Print%20Area.svg'; 
             else iconPath = '/images/SVG/Square%20Pen%20Print%20Area.svg';
        } else {
             iconPath = '/images/SVG/Pen%20Print%20Area.svg';
        }
    }
    // 8. Tumbler
    else if (p.includes('tumbler')) {
        iconPath = '/images/SVG/Tumbler Print Area.svg';
    }
    // 9. Jar
    else if (p.includes('jar')) {
        // Use the provided SVG for all areas for now
        iconPath = '/images/SVG/Jar%20Print%20Area.svg';
    }
    // 10. Lanyard
    else if (p.includes('lanyard')) {
        if (p.includes('dye')) {
             // Specific icon for Dye-Sublimated Lanyard
             iconPath = '/images/SVG/Dye-Sublimated%20Lanyard%20Print%20Area.svg';
        } else {
             // Default (Nylon) for others
             iconPath = '/images/SVG/Nylon%20Lanyard.svg';
        }
    }
    // 11. Lip Balm
    else if (p.includes('lip') || p.includes('balm')) {
        // Use the Lip Balm placeholder SVG
        iconPath = '/images/SVG/Lip%20Balm%20Print%20Area.svg';
    }

    // Render SVG or Fallback
    if (iconPath) {
        return (
            <img 
                src={iconPath} 
                alt={view} 
                className="w-full h-full object-contain p-1"
                onError={(e) => {
                    // Fallback on error (hide image to show text or generic icon?)
                    e.currentTarget.style.display = 'none';
                }}
            />
        );
    }

    // Generic Fallback SVG
    return (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
             <path d="M16 4L15 2H9L8 4L4 6V10L6 9V20C6 21 7 22 8 22H16C17 22 18 21 18 20V9L20 10V6L16 4Z" fill="none"/>
        </svg>
    );
};

export default function PrintAreaSelector() {
    const { printAreas, activePrintAreaId, setActivePrintArea, currentModel } = useConfiguratorStore();

    return (
        <div className="print-area-selector">
            {printAreas.map((area) => (
                <div 
                    key={area.id}
                    className="print-area-item"
                    onClick={() => setActivePrintArea(area.id)}
                >
                    <div className={`print-area-box ${activePrintAreaId === area.id ? 'active' : ''}`}>
                        <div className="print-area-icon">
                            <PrintAreaIcon view={area.name} productId={currentModel || 'unknown'} />
                        </div>
                    </div>
                    <span className={`print-area-name ${activePrintAreaId === area.id ? 'active' : ''}`}>
                        {area.name}
                    </span>
                </div>
            ))}
        </div>
    );
}
